import * as React from 'react';
import { Building, ChevronRight, Network } from 'lucide-react';
import { Table } from '@ixicare/ui';
import { t } from '~/i18n';
import { BillingRow, RECEIVER_GROUP, receiverCount } from './useBillingBreakdown';
import { deviceTypeLabel, profileGroup, profileTypeLabel, rowTypeLabel } from './billingLabels';
import { maxColour, SiteMaxCell } from './SiteMaxCell';
import styles from './BillingTable.module.css';

interface BillingTableProps {
  rows: BillingRow[];
  deviceTypes: string[];
  profileTypes: string[];
  /** Seat columns only appear when some row's organization has the billing model on. */
  showSeats: boolean;
  /** The third-party add-on column only appears when some row has a device on it. */
  showThirdParty: boolean;
}

const formatCount = (count: number): string => (count > 0 ? String(count) : '–');

interface Descendants {
  sites: number;
  orgs: number;
}

// Rows arrive depth-first, so an organization's descendants are exactly the rows
// after it that sit deeper than it does, up to the next row at its own level.
const countDescendants = (rows: BillingRow[]): Map<string, Descendants> => {
  const counts = new Map<string, Descendants>();
  rows.forEach((row, index) => {
    if (row.kind !== 'org') return;
    const found: Descendants = { sites: 0, orgs: 0 };
    for (let next = index + 1; next < rows.length && rows[next].depth > row.depth; next++) {
      if (rows[next].kind === 'org') found.orgs++;
      else found.sites++;
    }
    counts.set(row.key, found);
  });
  return counts;
};

export const BillingTable: React.FC<BillingTableProps> = ({
  rows,
  deviceTypes,
  profileTypes,
  showSeats,
  showThirdParty,
}) => {
  // Everything starts open: the table is an invoice breakdown, and a folded
  // branch on first load would hide lines someone is about to bill.
  const [collapsed, setCollapsed] = React.useState<Set<string>>(() => new Set());
  const descendants = React.useMemo(() => countDescendants(rows), [rows]);

  const visibleRows = React.useMemo(() => {
    const visible: BillingRow[] = [];
    let foldedAtDepth: number | null = null;
    for (const row of rows) {
      if (foldedAtDepth !== null && row.depth > foldedAtDepth) continue;
      foldedAtDepth = row.kind === 'org' && collapsed.has(row.key) ? row.depth : null;
      visible.push(row);
    }
    return visible;
  }, [rows, collapsed]);

  const toggle = (key: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.HeadCol>{t('billing.table.name')}</Table.HeadCol>
          {showSeats && <Table.HeadCol>{t('billing.seats.limit')}</Table.HeadCol>}
          {profileTypes.map((type) => (
            <Table.HeadCol key={`profile-${type}`}>{profileTypeLabel(type)}</Table.HeadCol>
          ))}
          {deviceTypes.map((type) => (
            <Table.HeadCol key={`device-${type}`}>{deviceTypeLabel(type)}</Table.HeadCol>
          ))}
          {showThirdParty && <Table.HeadCol>{t('billing.table.thirdPartyDevices')}</Table.HeadCol>}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {visibleRows.map((row) => {
          const profileCount = (group: string) =>
            row.usage.profiles.items
              .filter((item) => profileGroup(item.profileType) === group)
              .reduce((sum, item) => sum + item.count, 0);
          const deviceCount = (type: string) =>
            row.usage.devices.items.find((item) => item.deviceType === type)?.count ?? 0;
          const bold = row.kind === 'org' ? 600 : undefined;

          // Only a site carries a maximum of its own. An organization row shows the sum
          // over its sites — an invoice total, not a figure anyone sets there.
          const siteUuid = 'siteUuid' in row.scope ? row.scope.siteUuid : null;

          const folded = descendants.get(row.key);
          const hasChildren = !!folded && folded.sites + folded.orgs > 0;
          const isOpen = !collapsed.has(row.key);

          return (
            <Table.Row key={row.key}>
              <Table.Col style={{ fontWeight: bold }}>
                <span className={styles.nameCell}>
                  {row.depth > 0 && (
                    <span className={styles.guides} aria-hidden="true">
                      {Array.from({ length: row.depth }, (_, level) => (
                        <span key={level} className={styles.guide} />
                      ))}
                    </span>
                  )}
                  {row.kind === 'org' && hasChildren ? (
                    // Named after the branch it folds; aria-expanded carries the state,
                    // which is how a disclosure is announced ("Curando, collapsed").
                    <button
                      type="button"
                      className={styles.toggle}
                      aria-expanded={isOpen}
                      aria-label={row.name}
                      onClick={() => toggle(row.key)}
                    >
                      <ChevronRight size={14} strokeWidth={2.2} className={styles.chevron} />
                    </button>
                  ) : (
                    <span className={styles.toggleSpacer} />
                  )}
                  {/* The icon is the type now — the column it replaced was the only
                    other place saying org or site, so it keeps that label for
                    screen readers and on hover. */}
                  <span
                    className={styles.icon}
                    role="img"
                    aria-label={rowTypeLabel(row.kind)}
                    title={rowTypeLabel(row.kind)}
                  >
                    {row.kind === 'org' ? (
                      <Network size={15} strokeWidth={1.9} className={styles.orgIcon} />
                    ) : (
                      <Building size={15} strokeWidth={1.9} className={styles.siteIcon} />
                    )}
                  </span>
                  <span className={styles.name}>{row.name}</span>
                  {!isOpen && folded && (
                    <span
                      className={styles.foldChip}
                      aria-label={[
                        folded.sites > 0 && `${rowTypeLabel('site')}: ${folded.sites}`,
                        folded.orgs > 0 && `${rowTypeLabel('org')}: ${folded.orgs}`,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    >
                      {folded.sites > 0 && (
                        <span className={styles.foldCount} aria-hidden="true">
                          <Building size={12} strokeWidth={2} />
                          {folded.sites}
                        </span>
                      )}
                      {folded.orgs > 0 && (
                        <span className={styles.foldCount} aria-hidden="true">
                          <Network size={12} strokeWidth={2} />
                          {folded.orgs}
                        </span>
                      )}
                    </span>
                  )}
                </span>
              </Table.Col>
              {showSeats && (
                <Table.Col style={{ fontWeight: bold }}>
                  {siteUuid ? (
                    <SiteMaxCell
                      siteUuid={siteUuid}
                      value={row.usage.maxConcurrentAlarmReceivers}
                      alarmReceivers={row.usage.alarmReceivers}
                      editable={row.usage.concurrentBillingEnabled}
                    />
                  ) : row.usage.maxConcurrentAlarmReceivers === null ? (
                    t('billing.seats.noLimit')
                  ) : (
                    <span style={{ color: maxColour(row.usage.maxConcurrentAlarmReceivers, row.usage.alarmReceivers) }}>
                      {row.usage.maxConcurrentAlarmReceivers}
                    </span>
                  )}
                </Table.Col>
              )}
              {profileTypes.map((type) => {
                const isReceivers = type === RECEIVER_GROUP;
                const count = isReceivers ? receiverCount(row, profileCount(type)) : profileCount(type);
                return (
                  <Table.Col
                    key={`profile-${type}`}
                    title={
                      isReceivers && row.usage.concurrentBillingEnabled ? t('statistics.seats.peakHint') : undefined
                    }
                  >
                    {formatCount(count)}
                  </Table.Col>
                );
              })}
              {deviceTypes.map((type) => (
                <Table.Col key={`device-${type}`}>{formatCount(deviceCount(type))}</Table.Col>
              ))}
              {/* An add-on on top of the device columns: the same devices, counted again. */}
              {showThirdParty && <Table.Col>{formatCount(row.usage.thirdPartyDevices.totalCount)}</Table.Col>}
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};
